/*
 This file is part of GNU Taler
 (C) 2022-2024, 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 * Imports.
 */
import { base64FromArrayBuffer } from "../base64.js";
import { stringToBytes } from "../taler-crypto.js";
import {
  AccessToken,
  LongPollParams,
  PaginationParams,
} from "../types-taler-common.js";

/**
 * rfc8959
 * @param token
 * @returns
 */
/**
 * Escape a value for use as a single URL path segment.
 *
 * new URL() resolves structure rather than escaping it, so an unescaped "/"
 * injects extra segments and ".." eliminates the segment before it.
 */
export function pathSegment(x: string | number): string {
  return encodeURIComponent(x);
}

export function makeBearerTokenAuthHeader(token: AccessToken): string {
  return `Bearer ${token}`;
}

export type BasicOrTokenAuth = BasicAuth | TokenAuth;

export type BasicAuth = {
  type: "basic";
  username: string;
  password: string;
};

export type TokenAuth = {
  type: "bearer";
  token: AccessToken;
};

export function authHeaders(auth?: BasicOrTokenAuth): Record<string, string> {
  if (!auth) return {};
  switch (auth.type) {
    case "basic": {
      const credentials = `${auth.username}:${auth.password}`;
      const authEncoded: string = base64FromArrayBuffer(
        stringToBytes(credentials),
      );
      return {
        Authorization: `Basic ${authEncoded}`,
      };
    }
    case "bearer": {
      return {
        Authorization: makeBearerTokenAuthHeader(auth.token),
      };
    }
  }
}

export function addPaginationParams(url: URL, pagination?: PaginationParams) {
  if (!pagination) return;
  if (pagination.offset) {
    url.searchParams.set("offset", pagination.offset);
  }
  // Only send a limit when asked for: every field of PaginationParams is
  // optional, so a caller setting just a long-poll timeout would otherwise
  // override the server's default.
  if (pagination.limit !== undefined && pagination.limit !== 0) {
    const order = pagination.order === "asc" ? 1 : -1;
    url.searchParams.set("limit", String(order * Math.abs(pagination.limit)));
  }
}

export function addLongPollingParam(url: URL, param?: LongPollParams) {
  if (!param) return;
  if (param.timeoutMs) {
    url.searchParams.set("timeout_ms", String(param.timeoutMs));
  }
}

export interface CacheEvictor<T> {
  notifySuccess: (op: T) => Promise<void>;
}

export const nullEvictor: CacheEvictor<unknown> = {
  notifySuccess: () => Promise.resolve(),
};
